/**
 * Landing demo gallery: manual thumbnail paths, canonical URLs, and getDemoCaptures HTTP base.
 * Canonical path: public/demo-capture-thumbnails/{stem}/{stem}.jpg
 * — stem must match functions/src/demo.ts `buildDemoThumbnailStoragePath` (usually the demo splat filename
 * without extension, e.g. TowerBridgeRC.ply → TowerBridgeRC/TowerBridgeRC.jpg). Display titles like
 * "TOWER BRIDGE" alone can produce the wrong folder when titles were renamed in Firestore.
 */
const DEMO_THUMBNAIL_PATHS = {
  /** Must match Storage folder names exactly (case-sensitive). "Piper Archer III" is a different prefix folder. */
  'piper-archer-iii-sphere-demo':
    'public/demo-capture-thumbnails/Piper Archer III Sphere/Piper Archer III Sphere.jpg',
  /** Matches Storage: public/demo-capture-thumbnails/Piper Archer III Cockpit/Piper Archer III Cockpit.jpg */
  'piper-archer-iii-cockpit-demo':
    'public/demo-capture-thumbnails/Piper Archer III Cockpit/Piper Archer III Cockpit.jpg',
  'lycoming-demo': 'public/demo-capture-thumbnails/Lycoming/Lycoming.jpg',
  'nicefortsplat-demo': 'public/demo-capture-thumbnails/NiceFortSplat/NiceFortSplat.jpg',
  /** Align with Cloud Function buildDemoThumbnailStoragePath(title) when Firestore has no signed URL yet */
  'beach-demo': 'public/demo-capture-thumbnails/Beach/Beach.jpg',
  'spacex-launch-demo': 'public/demo-capture-thumbnails/SpaceX Launch/SpaceX Launch.jpg',
};

function getPublicStorageUrl(storagePath) {
  const bucket = process.env.REACT_APP_STORAGE_BUCKET;
  if (!bucket) return null;
  const encoded = encodeURIComponent(storagePath);
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encoded}?alt=media`;
}

/**
 * Turn a Firestore thumbnail value into a browser-loadable URL.
 * - Already-absolute http(s) URLs are returned as-is.
 * - Storage object paths (e.g. public/demo-capture-thumbnails/...) become Firebase download URLs.
 * - gs://bucket/path is supported.
 */
export function thumbnailStoragePathOrUrlToMediaUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const gs = /^gs:\/\/[^/]+\/(.+)$/.exec(trimmed);
  const path = gs ? gs[1] : trimmed;
  if (!path) return null;
  return getPublicStorageUrl(path);
}

/**
 * Returns the thumbnail URL for a demo capture if we have a manual link, else null.
 */
export function getManualDemoThumbnail(captureId) {
  const path = DEMO_THUMBNAIL_PATHS[captureId];
  if (!path) return null;
  return getPublicStorageUrl(path);
}

/**
 * Firestore often stores display titles in ALL CAPS; GCS object paths are case-sensitive and usually
 * match the splat filename (e.g. Piper Archer III Cockpit).
 */
function titleForCanonicalDemoThumbnailFolder(title) {
  const safe = title.replace(/[\\/]/g, '-').trim() || 'capture';
  if (safe.length < 2) return safe;
  /** Slug titles (e.g. Piper-archer-III-sphere) — Storage uses lowercase leading char; GCS paths are case-sensitive. */
  if (safe.includes('-') && /^[A-Z]/.test(safe)) {
    return safe.charAt(0).toLowerCase() + safe.slice(1);
  }
  const letters = /[A-Za-z]/.test(safe);
  const isAllCapsLine = letters && safe === safe.toUpperCase();
  if (!isAllCapsLine) return safe;
  return safe
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      if (word === 'iii' || word === 'ii' || word === 'iv' || word === 'i') return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Public Storage URL for canonical demo thumbnail path (matches backend buildDemoThumbnailStoragePath).
 * @param {string} title - Capture title as stored (e.g. folder name under demo-capture-thumbnails)
 */
export function getCanonicalDemoThumbnailUrlFromTitle(title) {
  if (!title || typeof title !== 'string') return null;
  const bucket = process.env.REACT_APP_STORAGE_BUCKET;
  if (!bucket) return null;
  const folderTitle = titleForCanonicalDemoThumbnailFolder(title);
  const path = `public/demo-capture-thumbnails/${folderTitle}/${folderTitle}.jpg`;
  return getPublicStorageUrl(path);
}

/**
 * Same path rule as Cloud Function `buildDemoThumbnailStoragePath(deriveDemoCaptureTitleFromFilename(splat))`.
 * Prefer this over title-based URLs when Firestore titles differ from Storage splat basenames.
 * @param {string | null | undefined} splatPath - e.g. public/demo-captures/TowerBridgeRC.ply
 */
export function getCanonicalDemoThumbnailUrlFromSplatPath(splatPath) {
  if (!splatPath || typeof splatPath !== 'string') return null;
  const filename = splatPath.split('/').pop() || '';
  const stem = filename.replace(/\.(sog|ply|ksplat|splat|spz)$/i, '').trim();
  if (!stem) return null;
  const safeTitle = stem.replace(/[\\/]/g, '-').trim() || 'capture';
  const path = `public/demo-capture-thumbnails/${safeTitle}/${safeTitle}.jpg`;
  return getPublicStorageUrl(path);
}

/** Base URL for the admin-curated demo gallery list (`getDemoCaptures` Cloud Function). */
export function getDemoCapturesUrl() {
  if (process.env.REACT_APP_GET_DEMO_CAPTURES_URL) {
    return process.env.REACT_APP_GET_DEMO_CAPTURES_URL;
  }
  const projectId = process.env.REACT_APP_PROJECT_ID;
  return projectId
    ? `https://europe-west1-${projectId}.cloudfunctions.net/getDemoCaptures`
    : null;
}
